import * as React from 'react';
import styled from 'styled-components';

import {
  Status,
  SearchOptInStateResponse,
  VerificationStates,
  notVerified,
} from '../../service/smsOptInStateService';
import { AddToast } from '../../types/airframe';

import { getTheme, Box, thc, Link } from '@flightstats/airframe-react';

const theme = getTheme();

export const checkOptInState = (state?: string) => {
  return Object.keys(VerificationStates).includes(state || '');
};

export const checkSearchResponseAndUpdate = (
  setOptInState: (state: Status) => void,
  addToast: AddToast,
  searchResponse?: SearchOptInStateResponse,
): Status | void => {
  if (Array.isArray(searchResponse)) {
    const firstSearchResultOptInState =
      searchResponse[0] && searchResponse[0].optInState;
    if (firstSearchResultOptInState) {
      if (checkOptInState(firstSearchResultOptInState)) {
        setOptInState(firstSearchResultOptInState);
        return firstSearchResultOptInState;
      } else {
        const message = 'Invalid optInState received';
        addToast(message, {
          variant: 'error',
        });
        console.error(`${message}: ${firstSearchResultOptInState}`);
      }
    }
  }
  //if not early-returned, fallback to not verified.
  setOptInState(notVerified);
  return notVerified;
};

interface VerificationStateProps {
  state: Status;
  disabled?: boolean;
  onSend: () => void;
  onResend: () => void;
  onTest: () => void;
}

const StatusWrapper = styled.div`
  margin: 10px;
`;

const StatusElement = styled.span<{ color: string }>`
  text-shadow: 2px 2px 3px RGBA(0, 0, 0, 0.5);
  color: ${props => props.color};
  padding-right: 4px;
  font-weight: 500;
`;

//to match testAlert button from contactPointsForm
const VerificationStateOuterWrapper = thc(Box, {
  width: '100%',
  mb: {
    xs: '20px',
    sm: '0',
  },
  mt: '5px',
  textAlign: {
    xs: 'left',
    sm: 'right',
  },
});

const LinkWrapper = styled.span`
  cursor: pointer;
`;

//to match testAlert button from contactPointsForm
const VerificationStateInnerWrapper = styled.div<{ backgroundColor: string }>`
  min-height: 35px;
  background-color: ${props => props.backgroundColor};
  width: 100%;
`;

const VerificationStateLabel = styled.span<{ color: string }>`
  font-weight: 700;
  line-height: 1.5rem;
  color: ${props => props.color};
  text-align: left;
`;

const OptInState: React.FC<VerificationStateProps> = ({
  state,
  disabled = false,
  onSend,
  onResend,
  onTest,
}) => {
  //Directly map statuses to their colors
  const stateColors = {
    [VerificationStates.PENDING]: disabled
      ? theme.colors.warningExtraDark //#C69214
      : theme.colors.warning, //#FFC72C
    [VerificationStates.VERIFIED]: disabled
      ? theme.colors.successExtraDark //#0e401c
      : theme.colors.success, //#24a148
    [VerificationStates.REJECTED]: disabled
      ? theme.colors.dangerExtraDark //#EB0000
      : theme.colors.danger, //#BA1B23
    [VerificationStates.NOT_VERIFIED]: disabled
      ? theme.colors.dangerExtraDark //#EB0000
      : theme.colors.danger, //#BA1B23
  };

  const stateMapping =
    VerificationStates[state] || VerificationStates.NOT_VERIFIED;

  const actions = {
      send: { displayName: 'send', handler: onSend },
      resend: { displayName: 'resend', handler: onResend },
      test: { displayName: 'test', handler: onTest },
    },
    //Directly map statuses to their actions
    stateActions = {
      [VerificationStates.PENDING]: actions.resend,
      [VerificationStates.VERIFIED]: actions.test,
      [VerificationStates.REJECTED]: actions.resend,
      [VerificationStates.NOT_VERIFIED]: actions.send,
    };

  if (!stateColors[stateMapping] || !stateActions[stateMapping]) {
    //log and error on invalid state
    throw new Error('invalid verification state');
  } else {
    const statusColor = stateColors[stateMapping];

    const verificationStateLabelColor = disabled
      ? theme.colors.grey50
      : theme.colors.fgOnLight;

    return (
      <>
        <VerificationStateOuterWrapper>
          <VerificationStateInnerWrapper backgroundColor={theme.colors.bgLight}>
            <VerificationStateLabel
              color={verificationStateLabelColor}
              className="airframe overrides"
            >
              Opt-in Status
            </VerificationStateLabel>
            <StatusWrapper className="airframe overrides airframe.overrides optInStatus">
              <StatusElement
                color={statusColor}
                className="airframe overrides airframe.overrides optInStatus"
              >
                {stateMapping}
              </StatusElement>
              (
              <LinkWrapper>
                <Link
                  onClick={disabled ? undefined : stateActions[stateMapping].handler}
                  disabled={disabled}
                >
                  {stateActions[stateMapping].displayName}
                </Link>
              </LinkWrapper>
              )
            </StatusWrapper>
          </VerificationStateInnerWrapper>
        </VerificationStateOuterWrapper>
      </>
    );
  }
};

export default OptInState;